import { useEffect, useMemo, useState } from 'react'
import type { Me } from '../lib/me'
import { can } from '../lib/me'
import {
  createNotice,
  editNotice,
  loadCommsNotices,
  retireNotice,
  reraiseNotice,
  translateNotice,
  loadNoticeLanguages,
  cardsFor,
  noticeWhen,
  NOTICE_STATUS,
  WELCOME_CARDS,
  type CommsNotice,
  type DbNotice,
  type NoticeLanguage,
  type NoticePlacement,
  type WelcomeCardKey,
} from '../lib/notices'
import { loadOrgUnits, type OrgUnitOption } from '../lib/admin'
import { messageOf } from '../lib/errors'
import { SUPPORTED_LOCALES } from '../lib/locale'
import { Picker } from './Picker'
import { FilterChips } from './FilterChips'
import { Dialog } from './Dialog'
import { NoticeStripItem } from './Notices'
import { WelcomeModal } from './WelcomeModal'

/**
 * Comms writes a notice, and takes one down.
 *
 * SHL-5 / CMP-1 — the thing FED-27 said was missing. Everything it calls was
 * built in 057 on top of 015's banner slot; nothing here invents a mechanism.
 *
 * ── THE END DATE IS REQUIRED-ISH AND CAPPED, AND THAT IS 015's DECISION ─────
 *
 * Blank means a fortnight. The maximum is 90 days and the database refuses
 * more. Both are deliberate: a banner with no expiry is the boolean pin 015
 * replaced, because in practice it is set once and never cleared. Wanting one
 * up for longer is a decision to make again in three months, not a date to
 * type once.
 *
 * ── THE LINK IS ALL-OR-NOTHING, AND INTERNAL ────────────────────────────────
 *
 * A button with no words, or words that go nowhere, are both refused server
 * side. So is an external URL — a CHECK constraint, not a client rule, because
 * this is the one place in the app where somebody other than the reader puts a
 * link in front of ~900 people. The field says so rather than failing at submit.
 *
 * ── EDITING DOES NOT PUT THE NOTICE BACK UP. "SHOW IT AGAIN" DOES ──────────
 *
 * THIS REVERSED ON 18 SEPTEMBER AND THE OLD VERSION IS WORTH KNOWING, because
 * the new one only makes sense against it.
 *
 * 059 built David's 12 Sep ruling: any change to the words, the button or the
 * pin bumps a revision by trigger, every dismissal below it stops applying, and
 * a notice 600 people had cleared returns for all 600. No opt-out — a "minor
 * correction" tickbox is one click away from an edit that reaches nobody, which
 * is OPS-13 in a new costume.
 *
 * Then 091 made a notice that can be a MODAL, and he reversed the consequence:
 * a typo fix must not re-show a modal to ~930 people. What did NOT change is
 * the trigger, which is the half that mattered — `revision` still moves on
 * every edit by any route, because a counter an editing function has to
 * remember to increment is one the second write path will not. What moved out
 * from under it is the interruption, into `reshowAtRevision`, which only
 * `reraise_notice()` touches.
 *
 * So there are now two acts on this screen where there was one, and the row
 * shows "Show it again" only while there is something to re-ask about
 * (`reshowAtRevision < revision`). The consequence is still stated before the
 * press rather than discovered after it; it has simply moved to the button that
 * causes it. The count beside each notice is the count for the CURRENT wording.
 *
 * ── EDITING IS ITS OWN FORM, NOT THE CREATE FORM IN A SECOND MODE ───────────
 *
 * A form that is sometimes Create and sometimes Save is how somebody publishes
 * a second copy of a notice they meant to fix. So there are still two forms —
 * `NoticeEditor` and the create form — and they open from two different
 * places with two different titles.
 *
 * ── THE BOARD IS CARDS, ONE NOTICE OPENS IN A TRAY, BOTH FORMS ARE MODALS ──
 *
 * David, 12 Oct: "new / edit notice should be a modal. existing notices should
 * be cards with detail in side tray." UNTIL THEN the create form sat open at
 * the top of the page and the editor opened in place under its row (059's
 * argument: edit the thing while looking at it). That put a form the height of
 * the screen between a comms admin and every notice they had posted.
 *
 * What 059's argument was protecting is kept, one level up: Edit is pressed in
 * the notice's own tray and opens OVER it, so the notice being changed is
 * still in view behind the form. Do not move the editor back under a row on
 * the strength of 059's comments.
 *
 * The board shows what is live or scheduled by default; what is over sits
 * behind "Past". A notice that is over is not reopened — "Post a copy" fills
 * the create form from it, dates blank.
 *
 * ── WHO SEES IT IS NOT EDITABLE, DELIBERATELY ───────────────────────────────
 *
 * `edit_notice` takes no audience. Re-aiming a notice that has already gone out
 * is not an edit: it removes it from people who have read it and pushes it at
 * people who never had it, with no record that either happened. That is a
 * take-down and a new notice, which is two visible acts instead of one silent
 * one.
 */
/**
 * A `yyyy-mm-dd` for a date input, in the READER'S zone.
 *
 * `toISOString().slice(0,10)` does NOT give this — that is UTC, so a notice
 * ending at 23:59 Vancouver shows as the next day. Shared by both forms since
 * 093 put a second date in each of them.
 */
function dayValue(iso: string): string {
  const d = new Date(iso)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

/**
 * The notice as the hub would render it, from a form's values. Whatever the
 * form holds right now — so a draft can be looked at before it is posted.
 * A button with only one half is left off, because the server would refuse it.
 */
function lookOf(v: {
  tag: string
  body: string
  ctaLabel: string
  ctaHref: string
  dismissible: boolean
  placement: NoticePlacement
  ctaKeys: WelcomeCardKey[]
}): DbNotice {
  const label = v.ctaLabel.trim()
  const href = v.ctaHref.trim()
  const both = label !== '' && href !== ''
  return {
    contentId: 'preview',
    tag: v.tag.trim() || null,
    body: v.body.trim(),
    ctaLabel: both ? label : null,
    ctaHref: both ? href : null,
    isDismissible: v.dismissible,
    placement: v.placement,
    ctaKeys: v.placement === 'modal' ? v.ctaKeys : [],
    variantLocale: 'en-CA',
    isFallback: false,
    endsAt: '',
  }
}

/**
 * "See how it looks" — David, 12 Oct, on this page rather than a trip to the
 * hub. In the tray, and in both forms, where it shows the words as typed.
 *
 * A STRIP opens in place, under the button: the hub's own component in its
 * `look` mode, so the preview cannot drift from the real thing. A MODAL opens
 * the real welcome modal over the page; its cards only close it. Neither
 * records a dismissal — closing a preview is not putting a notice away.
 */
function HowItLooks({ notice }: { notice: DbNotice }) {
  const [open, setOpen] = useState(false)
  const modal = notice.placement === 'modal'
  const empty = notice.body === ''
  const close = () => setOpen(false)
  return (
    <div className="noticelook">
      <button
        type="button"
        className="btn btn--quiet btn--sm"
        disabled={empty}
        title={empty ? 'Write the notice first.' : undefined}
        aria-expanded={modal ? undefined : open}
        aria-haspopup={modal ? 'dialog' : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {!modal && open ? 'Hide how it looks' : 'See how it looks'}
      </button>
      {!modal && open && !empty && (
        <div className="noticelook__stage">
          <ul className="notices" aria-label="How it looks">
            <NoticeStripItem n={notice} look />
          </ul>
          <p className="noticemgr__hint">
            As it shows at the top of the hub, to the people it is aimed at. The button and × do nothing here.
          </p>
        </div>
      )}
      {modal && open && !empty && (
        <WelcomeModal notice={notice} open onClose={close} onNavigate={close} onOpenDrawer={close} />
      )}
    </div>
  )
}

/**
 * The edit form for one notice, opened as a modal over its tray (12 Oct).
 *
 * Seeded from the row it is editing and sending every field back, because
 * edit_notice is a REPLACE (049's rule).
 *
 * ── THE START DATE IS SENT BACK VERBATIM ONCE IT HAS PASSED ─────────────────
 *
 * David, 20 Sep: a start that has arrived is a fact about what happened, not a
 * plan. Moving it later un-shows something people have already read, leaves
 * every dismissal exactly where it is, and records nothing — the edit trigger
 * says the WORDING moved, and it did not.
 *
 * The server refuses the change. This form does not offer it: the input is
 * disabled and the row's own `startsAt` goes back UNCHANGED, which is what
 * "leave it alone" has to look like in a replace that takes no defaults. A
 * re-derived midnight would differ from the stored instant and be refused,
 * which is why the ISO string is kept rather than the day.
 */
function NoticeEditor({
  notice,
  busy,
  onCancel,
  onSave,
}: {
  notice: CommsNotice
  busy: boolean
  onCancel: () => void
  onSave: (values: {
    body: string
    tag: string
    startsAt: string
    endsAt: string | null
    dismissible: boolean
    ctaLabel: string | null
    ctaHref: string | null
  }) => void
}) {
  // Only a notice that has not started may be rescheduled. `status` is the
  // server's, so this agrees with what the server will accept — comparing
  // `startsAt` to `Date.now()` here would be the reader's clock deciding.
  const started = notice.status !== 'pending'
  const [startsAt, setStartsAt] = useState(() => dayValue(notice.startsAt))
  const [body, setBody] = useState(notice.body)
  const [tag, setTag] = useState(notice.tag ?? '')
  const [ctaLabel, setCtaLabel] = useState(notice.ctaLabel ?? '')
  const [ctaHref, setCtaHref] = useState(notice.ctaHref ?? '')
  const [dismissible, setDismissible] = useState(notice.isDismissible)
  const [endsAt, setEndsAt] = useState(() => dayValue(notice.endsAt))

  const trimmed = body.trim()
  const linkHalf = (ctaHref.trim() === '') !== (ctaLabel.trim() === '')
  // Whether anything actually changed. A Save that writes an identical row
  // would still bump nothing (the triggers compare values) — but offering it
  // suggests it would, and "I pressed Save and it did not come back" is a worse
  // thing to explain than a disabled button.
  const dirty =
    trimmed !== notice.body ||
    tag.trim() !== (notice.tag ?? '') ||
    ctaLabel.trim() !== (notice.ctaLabel ?? '') ||
    ctaHref.trim() !== (notice.ctaHref ?? '') ||
    dismissible !== notice.isDismissible ||
    startsAt !== dayValue(notice.startsAt)
  const canSave = trimmed !== '' && !linkHalf && !busy

  return (
    <form
      className="noticemgr__edit"
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSave) return
        onSave({
          body: trimmed,
          tag: tag.trim(),
          // VERBATIM once it has started. Re-deriving midnight from the day
          // would differ from the stored instant, and the server compares with
          // `is distinct from` — so a body-only edit would be refused for
          // moving a date nobody touched.
          startsAt: started
            ? notice.startsAt
            : new Date(`${startsAt}T00:00:00`).toISOString(),
          endsAt: new Date(`${endsAt}T23:59:59`).toISOString(),
          dismissible,
          ctaLabel: ctaLabel.trim() || null,
          ctaHref: ctaHref.trim() || null,
        })
      }}
    >
      <div className="noticemgr__row">
        <label className="admin__field">
          <span className="admin__searchlabel">Label</span>
          <input value={tag} maxLength={24} onChange={(e) => setTag(e.target.value)} />
        </label>
        <label className="admin__field">
          <span className="admin__searchlabel">Starts</span>
          <input
            type="date"
            value={startsAt}
            disabled={started}
            onChange={(e) => setStartsAt(e.target.value)}
          />
          {/* Said rather than discovered. A disabled input with no reason reads
              as a fault; the server refuses this and the person would find out
              at Save. */}
          <span className="noticemgr__hint">
            {started
              ? 'It has already started, so this is fixed. Take it down to stop it.'
              : 'It has not gone out yet, so this can still move.'}
          </span>
        </label>
        <label className="admin__field">
          <span className="admin__searchlabel">Show until</span>
          <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </label>
      </div>

      <label className="admin__field">
        <span className="admin__searchlabel">Notice</span>
        <textarea
          className="composer__body"
          rows={3}
          maxLength={600}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>

      <div className="noticemgr__row">
        <label className="admin__field">
          <span className="admin__searchlabel">Button</span>
          <input value={ctaLabel} maxLength={40} onChange={(e) => setCtaLabel(e.target.value)} />
        </label>
        <label className="admin__field">
          <span className="admin__searchlabel">Goes to</span>
          <input value={ctaHref} onChange={(e) => setCtaHref(e.target.value)} />
        </label>
      </div>

      {linkHalf && (
        <p className="noticemgr__warn">A button needs both wording and a destination, or neither.</p>
      )}

      {/* The same two-state toggle the row controls use (041), not a checkbox:
          one property with two values, and the control shows the state as well
          as the offer. */}
      <div className="noticemgr__row">
        <button
          type="button"
          className={`btn btn--xs ${dismissible ? 'btn--primary' : 'btn--quiet'}`}
          onClick={() => setDismissible(true)}
        >
          Can be dismissed
        </button>
        <button
          type="button"
          className={`btn btn--xs ${!dismissible ? 'btn--primary' : 'btn--quiet'}`}
          onClick={() => setDismissible(false)}
        >
          Pinned until it expires
        </button>
      </div>

      {/* THIS SENTENCE WAS THE OPPOSITE UNTIL 091, AND LEAVING IT WOULD HAVE
          BEEN THE WORST VERSION OF STANDING RULE 4: a screen describing a
          control that does something else.

          It used to read "Saving puts this back in front of the N people who
          have already dismissed it", which was true and was David's 12 Sep
          ruling. On 18 September, looking at a modal, he reversed it — a typo
          fix must not re-interrupt ~930 people — so saving now leaves them
          alone and "Show it again" is a separate, deliberate press.

          Still said BEFORE the press rather than discovered after it. The
          number is the same one; what changed is which button it belongs to. */}
      <HowItLooks
        notice={lookOf({
          tag,
          body,
          ctaLabel,
          ctaHref,
          dismissible,
          placement: notice.placement,
          ctaKeys: notice.ctaKeys,
        })}
      />

      {dirty && notice.dismissedCount > 0 && (
        <p className="noticemgr__warn">
          The {notice.dismissedCount}{' '}
          {notice.dismissedCount === 1 ? 'person who has' : 'people who have'} already
          dismissed this will not see it again. Use “Show it again” if they should.
        </p>
      )}

      <div className="noticemgr__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={!canSave || !dirty}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  )
}

/** A day, never a time — 044's rule about schedules. */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString()
}

/**
 * THE BOARD'S TWO SHELVES, AND THE SERVER'S STATUS DECIDES WHICH (12 Oct).
 *
 * What is up or still to come is what a comms admin came to look at; what is
 * over is history. Both are a reading of `n.status`, never of the dates against
 * the browser clock — 093's rule, for the same reason: the hub answers with the
 * database's clock and the board must agree with it at the boundary.
 */
type Shelf = 'current' | 'past'
function onShelf(n: CommsNotice, shelf: Shelf): boolean {
  const current = n.status === 'live' || n.status === 'pending'
  return shelf === 'current' ? current : !current
}

/** The pills a notice carries on its card and at the top of its tray. */
function NoticePills({ n }: { n: CommsNotice }) {
  return (
    <span className="noticecard__pills">
      {/* FOUR STATES, FROM THE SERVER — 093. The map is in lib/notices.ts and
          the STATE is decided in `app.notice_status`, not here. */}
      <span className={NOTICE_STATUS[n.status].pill}>{NOTICE_STATUS[n.status].label}</span>
      {n.placement === 'modal' && <span className="pill pill--kind">Sign-in modal</span>}
      {!n.isDismissible && <span className="pill pill--draft">Pinned</span>}
    </span>
  )
}

/**
 * One notice on the board. The whole card is the button that opens its tray —
 * a card with a separate "Details" link is two targets for one act.
 */
function NoticeCard({ n, onOpen }: { n: CommsNotice; onOpen: () => void }) {
  // EVERY STATE POINTS AT A DIFFERENT DATE (093): pending at the start, live at
  // the end, taken down at the day somebody pulled it. `noticeWhen` is the one
  // place that mapping lives.
  const when = noticeWhen(n)
  return (
    <li>
      <button type="button" className="noticecard" aria-haspopup="dialog" onClick={onOpen}>
        <NoticePills n={n} />
        {n.tag && <span className="noticecard__tag">{n.tag}</span>}
        <span className="noticecard__body">{n.body}</span>
        <span className="noticecard__meta">
          {n.audience.length === 0 ? 'Everyone' : n.audience.join(', ')}
          {' · '}
          {when.verb} {day(when.iso)}
          {' · '}
          {/* A count, never a list. The database will not tell us who (057). */}
          {n.dismissedCount} dismissed
        </span>
      </button>
    </li>
  )
}

/** A language by its own name, or its code for one the app does not offer yet. */
function languageName(locale: string): string {
  return SUPPORTED_LOCALES.find((l) => l.key === locale)?.label ?? locale
}

/** What each status means to the person who wrote the notice (180). */
const LANGUAGE_STATUS: Record<NoticeLanguage['status'], string> = {
  original: 'as written',
  machine: 'translated by One',
  reviewed: 'translation reviewed',
  approved: 'translation approved',
  missing: 'not translated yet',
}

/**
 * Everything about one notice, in the right-hand tray (David, 12 Oct). The card
 * says what it is; this says everything else, and holds what can be done to it.
 */
function NoticeDetail({
  n,
  busy,
  languages,
  translating,
  onTranslate,
  onEdit,
  onShowAgain,
  onTakeDown,
  onCopy,
}: {
  n: CommsNotice
  busy: boolean
  /** 180. Undefined while the read is on its way. */
  languages: NoticeLanguage[] | undefined
  translating: boolean
  onTranslate: () => void
  onEdit: () => void
  onShowAgain: () => void
  onTakeDown: () => void
  onCopy: () => void
}) {
  const cards = cardsFor(n.ctaKeys)
  return (
    <div className="noticetray">
      <NoticePills n={n} />
      <p className="noticetray__body">{n.body}</p>
      <HowItLooks notice={n} />

      {/* ACTIONS ON WHAT IS STILL TO COME AS WELL AS WHAT IS SHOWING (093). A
          scheduled notice you cannot edit or cancel is standing rule 4 — a
          write path with no surface. `retire_notice` handles a pending one: it
          revokes any promotion with time left on it, and a pending one has all
          of it. */}
      {(n.status === 'live' || n.status === 'pending') && (
        <div className="noticemgr__actions">
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={onEdit}>
            Edit
          </button>
          {/* THE DELIBERATE RE-SHOW. David, 18 Sep. Rendered only when there is
              something to re-ask about — `reshowAtRevision < revision` means
              "edits nobody has been re-interrupted for". When they are equal the
              server refuses (rule 12), so a button here would always error.
              NOT ON A PENDING NOTICE (093): nobody has seen it yet. */}
          {n.status === 'live' && n.reshowAtRevision < n.revision && (
            <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={onShowAgain}>
              Show it again
            </button>
          )}
          <button type="button" className="btn btn--danger btn--sm" disabled={busy} onClick={onTakeDown}>
            {/* One act, `retire_notice`, two honest names for it. "Take down"
                on something nobody has seen describes a thing that did not
                happen. Not plain "Cancel" in a tray, where it reads as close. */}
            {n.status === 'pending' ? 'Cancel this notice' : 'Take down'}
          </button>
        </div>
      )}

      <dl className="noticetray__facts">
        <dt>Who sees it</dt>
        <dd>{n.audience.length === 0 ? 'Everyone' : n.audience.join(', ')}</dd>

        <dt>Starts</dt>
        <dd>{day(n.startsAt)}</dd>

        <dt>Shows until</dt>
        <dd>{day(n.endsAt)}</dd>

        {n.revokedAt && (
          <>
            <dt>Taken down</dt>
            <dd>{day(n.revokedAt)}</dd>
          </>
        )}

        <dt>How it appears</dt>
        <dd>
          {n.placement === 'modal' ? 'A modal at sign-in' : 'A strip above the hub'}
          {n.placement === 'modal' && (
            <span className="noticemgr__hint">
              {' — '}
              {cards.length === 0 ? 'your words and no cards' : cards.map((c) => c.title).join(', ')}
            </span>
          )}
        </dd>

        <dt>Button</dt>
        <dd>
          {n.ctaLabel && n.ctaHref ? (
            <>
              “{n.ctaLabel}” <span className="noticemgr__hint">goes to {n.ctaHref}</span>
            </>
          ) : (
            <span className="noticemgr__hint">None</span>
          )}
        </dd>

        {/* 180 (David, 12 Oct: "make sure notices are also translated"). One
            translates a notice into every language the company reads, on Post
            and on every Save; this says whether it has. A language still
            missing on something live or scheduled gets a button, because the
            alternative is those readers getting the original, labelled. */}
        <dt>Languages</dt>
        <dd>
          {languages === undefined ? (
            <span className="noticemgr__hint">Checking…</span>
          ) : (
            <span className="noticetray__langs">
              {languages.map((l) => (
                <span key={l.locale}>
                  {languageName(l.locale)}{' '}
                  <span className="noticemgr__hint">— {translating && l.status === 'missing' ? 'translating…' : LANGUAGE_STATUS[l.status]}</span>
                </span>
              ))}
              {languages.some((l) => l.status === 'missing') && (
                <button
                  type="button"
                  className="btn btn--quiet btn--xs"
                  disabled={busy || translating}
                  onClick={onTranslate}
                >
                  {translating ? 'Translating…' : 'Translate now'}
                </button>
              )}
            </span>
          )}
        </dd>

        <dt>Dismissing</dt>
        <dd>{n.isDismissible ? 'People can put it away' : 'Pinned until it expires'}</dd>

        {/* The count for the CURRENT wording, not for all time (091). Said so
            once it has been edited, because that is when the two differ. */}
        <dt>Dismissed</dt>
        <dd>
          {n.dismissedCount} {n.dismissedCount === 1 ? 'person' : 'people'}
          {n.revision > 1 && <span className="noticemgr__hint"> — this wording, edited {n.revision - 1}×</span>}
        </dd>

        <dt>Posted</dt>
        <dd>
          {n.authorName ?? 'Someone no longer here'} · {day(n.publishedAt)}
        </dd>
      </dl>

      {/* A NOTICE THAT IS OVER IS NOT REOPENED — it is posted again. Re-opening
          one would put back something people have already seen go, under the
          same dismissals. A copy is a new notice: new window, new count. */}
      <div className="noticetray__foot">
        {!(n.status === 'live' || n.status === 'pending') && (
          <p className="noticemgr__hint">It is over and cannot be changed. Post a copy to use it again.</p>
        )}
        <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={onCopy}>
          Post a copy
        </button>
      </div>
    </div>
  )
}

export function NoticeManager({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const [rows, setRows] = useState<CommsNotice[] | null>(null)
  const [units, setUnits] = useState<OrgUnitOption[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * THREE SURFACES OVER THE BOARD (David, 12 Oct): the tray for one notice, the
   * edit modal over that tray, and the new-notice modal. Whichever is on top
   * shows the error — a message rendered on the page under a scrim is one
   * nobody reads.
   */
  const [shelf, setShelf] = useState<Shelf>('current')
  const [openId, setOpenId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [creating, setCreating] = useState(false)
  /** 180. Each notice's languages, and which are being translated right now. */
  const [langs, setLangs] = useState<Map<string, NoticeLanguage[]> | null>(null)
  const [translating, setTranslating] = useState<ReadonlySet<string>>(new Set())

  const [body, setBody] = useState('')
  const [tag, setTag] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [dismissible, setDismissible] = useState(true)
  const [ctaLabel, setCtaLabel] = useState('')
  const [ctaHref, setCtaHref] = useState('')
  const [audience, setAudience] = useState<string[]>([])
  const [placement, setPlacementState] = useState<NoticePlacement>('banner')
  const [ctaKeys, setCtaKeys] = useState<WelcomeCardKey[]>([])

  /**
   * FLIPPING BACK TO A BANNER CLEARS THE CARDS, AND IT HAS TO HAPPEN HERE.
   *
   * `banner_cards_are_modal_only` refuses a banner carrying `cta_keys`, so
   * leaving them behind means Post fails on a constraint naming a column the
   * person never saw — and the cards are not on screen at that point, because
   * the picker only renders for a modal. Doing it in the setter rather than at
   * submit means there is no moment where the state is a thing the database
   * would reject.
   */
  function setPlacement(next: NoticePlacement) {
    setPlacementState(next)
    if (next !== 'modal') setCtaKeys([])
  }

  // 015 split these deliberately: content.publish lets you speak to the whole
  // company, audience.build lets you single out a crew. Somebody holding only
  // the first gets no audience picker rather than a picker that fails on submit.
  const canTarget = can(me, 'audience.build')

  async function refresh() {
    setRows(await loadCommsNotices())
  }

  async function refreshLangs(): Promise<Map<string, NoticeLanguage[]>> {
    const m = await loadNoticeLanguages().catch(() => new Map<string, NoticeLanguage[]>())
    setLangs(m)
    return m
  }

  /**
   * 180. Ask One to translate these notices, then re-read their languages.
   * Fire-and-forget from every caller: a translation that does not arrive
   * leaves readers the original, labelled, and the tray says so.
   */
  async function translate(ids: string[]) {
    if (ids.length === 0) return
    setTranslating((cur) => new Set([...cur, ...ids]))
    await Promise.all(ids.map((id) => translateNotice(id)))
    setTranslating((cur) => new Set([...cur].filter((x) => !ids.includes(x))))
    await refreshLangs()
  }

  useEffect(() => {
    let alive = true
    loadCommsNotices()
      .then((r) => alive && setRows(r))
      .catch((e: Error) => alive && setError(e.message))
    /* 180. ONCE PER BOARD LOAD, anything live or scheduled that is missing a
       language is sent to be translated. This is the backfill: notices posted
       before 180, and any whose translation failed, catch up the next time
       comms opens the board. notice_languages lists `missing` only for live
       and pending notices, and the job translates only what is missing, so a
       board with nothing to do costs one read. */
    void refreshLangs().then((m) => {
      if (!alive) return
      const due = [...m.entries()].filter(([, l]) => l.some((x) => x.status === 'missing')).map(([id]) => id)
      void translate(due)
    })
    if (canTarget) {
      loadOrgUnits()
        .then((u) => alive && setUnits(u))
        .catch(() => undefined)
    }
    return () => {
      alive = false
    }
  }, [canTarget])

  const options = useMemo(
    () =>
      units.map((u) => ({
        value: u.orgUnitId,
        label: u.name,
        depth: u.depth,
        hint: u.path,
      })),
    [units],
  )

  const trimmed = body.trim()
  const linkHalf = (ctaHref.trim() === '') !== (ctaLabel.trim() === '')
  const canSubmit = trimmed !== '' && !linkHalf && !busy

  const opened = rows?.find((r) => r.contentId === openId) ?? null
  const shown = (rows ?? []).filter((r) => onShelf(r, shelf))
  const currentCount = (rows ?? []).filter((r) => onShelf(r, 'current')).length
  const pastCount = (rows ?? []).length - currentCount

  function resetForm() {
    setBody('')
    setTag('')
    setStartsAt('')
    setEndsAt('')
    setCtaLabel('')
    setCtaHref('')
    setAudience([])
    setDismissible(true)
    setPlacement('banner')
  }

  /**
   * "Post a copy" — the form filled from a notice, dates left blank, because a
   * window is the one thing a copy must be asked for again. The audience goes
   * across by name where this person can still aim at that unit; the board
   * only knows names, so a unit since renamed is left for them to pick.
   */
  function copyFrom(n: CommsNotice) {
    setBody(n.body)
    setTag(n.tag ?? '')
    setStartsAt('')
    setEndsAt('')
    setCtaLabel(n.ctaLabel ?? '')
    setCtaHref(n.ctaHref ?? '')
    setDismissible(n.isDismissible)
    setAudience(
      canTarget
        ? n.audience
            .map((name) => units.find((u) => u.name === name)?.orgUnitId)
            .filter((id): id is string => !!id)
        : [],
    )
    setPlacement(n.placement)
    if (n.placement === 'modal') setCtaKeys(n.ctaKeys)
    setError(null)
    setOpenId(null)
    setCreating(true)
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const id = await createNotice({
        body: trimmed,
        tag: tag.trim(),
        // A date input gives a day, not an instant. The START of that day and
        // the END of the other, both in the reader's own zone: a notice set to
        // end "on the 20th" that vanishes at midnight UTC disappears
        // mid-afternoon in Lima. Blank start means now, which is what every
        // notice did before 093.
        startsAt: startsAt ? new Date(`${startsAt}T00:00:00`).toISOString() : null,
        endsAt: endsAt ? new Date(`${endsAt}T23:59:59`).toISOString() : null,
        dismissible,
        ctaLabel: ctaLabel.trim() || null,
        ctaHref: ctaHref.trim() || null,
        audience,
        /**
         * THIS FORM CAN NOW MAKE A MODAL. REVERSED BY DAVID ON 20 SEPTEMBER,
         * AND THE OLD REASON IS KEPT BECAUSE IT SETS THE CONDITIONS.
         *
         * WHAT IT SAID, until 20 Sep: this form makes banners and there is
         * deliberately no control for the other kind. A modal stops ~930 people
         * and takes a decision about which four cards they see; the two that
         * exist are the 7 and 13 October welcomes, and they are SEEDED
         * (supabase/seed/008), which is 020's precedent — re-running the seed
         * is the write path. So it was not standing rule 4's "write path with
         * no surface": the surface was the seed file, and a modal is not a
         * thing to make casually from a text box at 4pm.
         *
         * DO NOT PUT THAT BACK on the strength of reading it. It was a
         * judgement about who should be able to make one, not a constraint, and
         * the person it was protecting reversed it.
         *
         * WHAT SURVIVES IT, because the argument was right about these:
         *   - The seed is STILL the write path for the two welcome notices. It
         *     is idempotent and deterministic-id; this form makes NEW ones.
         *   - `placement` and `ctaKeys` are CREATE-TIME ONLY. `editNotice` does
         *     not take them and its signature must not gain them — rule 9's
         *     stable-key clause, and 057's reason for freezing the audience:
         *     turning a banner into a modal after four hundred people have
         *     dismissed it re-shapes something that has already gone out.
         *   - The control is set apart and says what a modal does, because it
         *     interrupts everybody. It is not a toggle beside the tag field.
         */
        placement,
        ctaKeys,
      })
      resetForm()
      setCreating(false)
      await refresh()
      // What was just posted, in its tray: the confirmation is the notice
      // itself, as the server now has it, not a toast saying it worked.
      setShelf('current')
      setOpenId(id)
      onChanged()
      void translate([id])
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  async function save(
    n: CommsNotice,
    v: {
      body: string
      tag: string
      startsAt: string
      endsAt: string | null
      dismissible: boolean
      ctaLabel: string | null
      ctaHref: string | null
    },
  ) {
    setBusy(true)
    setError(null)
    try {
      await editNotice(n.contentId, v)
      setEditing(false)
      await refresh()
      // 180: the edit dropped the machine translations of the old words.
      void translate([n.contentId])
      // The strip above has to re-read too, or a comms admin looking at a stale
      // strip would conclude the edit had not landed.
      onChanged()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Put an edited notice back in front of everybody who dismissed an older
   * wording. The deliberate half of David's 18 Sep ruling.
   *
   * CONFIRMED, and the confirmation names the NUMBER. Taking a notice down
   * affects nobody's attention; this interrupts a specific count of people who
   * had finished with it, and at ~930 that is the one press on this screen
   * worth pausing over.
   */
  async function showAgain(n: CommsNotice) {
    if (
      !window.confirm(
        `Show this to the ${n.dismissedCount} ${
          n.dismissedCount === 1 ? 'person who has' : 'people who have'
        } already dismissed it?`,
      )
    )
      return
    setBusy(true)
    setError(null)
    try {
      await reraiseNotice(n.contentId)
      await refresh()
      onChanged()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  async function takeDown(n: CommsNotice) {
    // A pending notice has not shown to anybody, so "it stops showing for
    // everyone straight away" would be describing something that never
    // happened — and the thing being cancelled is different enough to be worth
    // different words.
    const warning =
      n.status === 'pending'
        ? 'Cancel this notice? It has not gone out yet, and this cannot be undone — you would have to post it again.'
        : 'Take this notice down? It stops showing for everyone straight away.'
    if (!window.confirm(warning)) return
    setBusy(true)
    setError(null)
    try {
      await retireNotice(n.contentId)
      // The tray stays open on it: the pill turning to "Taken down" is the
      // confirmation, and the board behind has already moved it to Past.
      await refresh()
      onChanged()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const errorLine = error && (
    <p className="roles__error" role="alert">
      {error}
    </p>
  )
  const on = editing && opened ? 'edit' : creating ? 'create' : opened ? 'tray' : 'page'

  return (
    <div className="noticemgr">
      {on === 'page' && errorLine}

      <div className="noticemgr__toolbar">
        {/* The same two-state toggle the form uses (041): one property, two
            values, and the control shows the state as well as the offer. */}
        <div className="noticemgr__actions" role="group" aria-label="Which notices">
          <button
            type="button"
            className={`btn btn--xs ${shelf === 'current' ? 'btn--primary' : 'btn--quiet'}`}
            aria-pressed={shelf === 'current'}
            onClick={() => setShelf('current')}
          >
            Live and scheduled{rows ? ` (${currentCount})` : ''}
          </button>
          <button
            type="button"
            className={`btn btn--xs ${shelf === 'past' ? 'btn--primary' : 'btn--quiet'}`}
            aria-pressed={shelf === 'past'}
            onClick={() => setShelf('past')}
          >
            Past{rows ? ` (${pastCount})` : ''}
          </button>
        </div>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          aria-haspopup="dialog"
          onClick={() => {
            setError(null)
            setCreating(true)
          }}
        >
          New notice
        </button>
      </div>

      {rows === null ? (
        <p className="empty" aria-busy="true">
          Loading…
        </p>
      ) : shown.length === 0 ? (
        <p className="empty">
          {rows.length === 0
            ? 'Nothing has been posted yet.'
            : shelf === 'current'
              ? 'Nothing is live or scheduled.'
              : 'Nothing has ended or been taken down yet.'}
        </p>
      ) : (
        <ul className="noticemgr__cards">
          {shown.map((n) => (
            <NoticeCard
              key={n.contentId}
              n={n}
              onOpen={() => {
                setError(null)
                setOpenId(n.contentId)
              }}
            />
          ))}
        </ul>
      )}

      {/* ── THE TRAY ─────────────────────────────────────────────────────── */}
      {opened && (
        <Dialog
          open
          title={opened.tag || 'Notice'}
          variant="tray"
          className="noticetray__panel"
          onClose={() => {
            setOpenId(null)
            setEditing(false)
            setError(null)
          }}
        >
          {on === 'tray' && errorLine}
          <NoticeDetail
            n={opened}
            busy={busy}
            languages={langs?.get(opened.contentId) ?? (langs ? [] : undefined)}
            translating={translating.has(opened.contentId)}
            onTranslate={() => void translate([opened.contentId])}
            onEdit={() => {
              setError(null)
              setEditing(true)
            }}
            onShowAgain={() => void showAgain(opened)}
            onTakeDown={() => void takeDown(opened)}
            onCopy={() => copyFrom(opened)}
          />
        </Dialog>
      )}

      {/* ── EDIT, A MODAL OVER THE TRAY ──────────────────────────────────── */}
      {editing && opened && (
        <Dialog
          open
          title={`Edit ${opened.tag ? `“${opened.tag}”` : 'notice'}`}
          className="noticedialog"
          onClose={() => {
            setEditing(false)
            setError(null)
          }}
        >
          {on === 'edit' && errorLine}
          <NoticeEditor
            // Keyed on the revision so a successful save rebuilds the form from
            // the row that came back, rather than leaving the old values
            // sitting in state under a notice that has moved on.
            key={opened.revision}
            notice={opened}
            busy={busy}
            onCancel={() => setEditing(false)}
            onSave={(v) => void save(opened, v)}
          />
        </Dialog>
      )}

      {/* ── NEW NOTICE, A MODAL ──────────────────────────────────────────
          The draft lives in this component, not the dialog, so × or Escape
          closes it WITHOUT losing what was typed — reopening "New notice"
          brings it back. Discard is the deliberate way to throw it away. The
          scrim is held shut once something is typed (Dialog, 29 Sep). */}
      <Dialog
        open={creating}
        title="New notice"
        className="noticedialog"
        onClose={() => {
          setCreating(false)
          setError(null)
        }}
      >
        {on === 'create' && errorLine}
        <form
          className="noticemgr__form"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) void submit()
          }}
        >
          <div className="noticemgr__row">
            <label className="admin__field">
              <span className="admin__searchlabel">Label</span>
              <input
                value={tag}
                maxLength={24}
                placeholder="Rebrand FAQ"
                onChange={(e) => setTag(e.target.value)}
              />
            </label>

            <label className="admin__field">
              <span className="admin__searchlabel">Starts</span>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
              <span className="noticemgr__hint">Blank is straight away.</span>
            </label>

            <label className="admin__field">
              <span className="admin__searchlabel">Show until</span>
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
              <span className="noticemgr__hint">
                Blank is two weeks from the start. 90 days is the most.
              </span>
            </label>
          </div>

          <label className="admin__field">
            <span className="admin__searchlabel">Notice</span>
            <textarea
              className="composer__body"
              rows={3}
              maxLength={600}
              value={body}
              placeholder="What everyone needs to know."
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          <div className="noticemgr__row">
            <label className="admin__field">
              <span className="admin__searchlabel">Button</span>
              <input
                value={ctaLabel}
                maxLength={40}
                placeholder="Read the rebrand FAQ"
                onChange={(e) => setCtaLabel(e.target.value)}
              />
            </label>
            <label className="admin__field">
              <span className="admin__searchlabel">Goes to</span>
              <input
                value={ctaHref}
                placeholder="#/wiki?q=rebrand"
                onChange={(e) => setCtaHref(e.target.value)}
              />
              <span className="noticemgr__hint">Somewhere in the hub. External links are not allowed.</span>
            </label>
          </div>

          {linkHalf && (
            <p className="noticemgr__warn">
              A button needs both wording and a destination, or neither.
            </p>
          )}

          {canTarget && (
            <div className="noticemgr__row">
              <Picker
                label="Who sees it"
                options={options}
                selected={audience}
                onChange={setAudience}
                anyLabel="Everyone"
                searchPlaceholder="Search business units"
              />
              <FilterChips
                chips={audience.map((id) => ({
                  value: id,
                  label: units.find((u) => u.orgUnitId === id)?.name ?? id,
                  group: 'audience',
                }))}
                onRemove={(c) => setAudience((cur) => cur.filter((x) => x !== c.value))}
                onClear={() => setAudience([])}
              />
            </div>
          )}

          {/* ── HOW IT APPEARS ──────────────────────────────────────────────
              SET APART ON PURPOSE. A modal stops everybody at sign-in and takes
              a decision about which cards they see; David reversed 091's
              withholding of this control on 20 Sep, and what he did not reverse
              is that it should feel like a decision. It is its own block with its
              own heading, at the end of the form, rather than a toggle beside the
              label field. */}
          <div className="noticemgr__placement">
            <p className="noticemgr__heading">How it appears</p>
            <div className="noticemgr__actions">
              <button
                type="button"
                className={`btn btn--xs ${placement === 'banner' ? 'btn--primary' : 'btn--quiet'}`}
                onClick={() => setPlacement('banner')}
              >
                A strip above the hub
              </button>
              <button
                type="button"
                className={`btn btn--xs ${placement === 'modal' ? 'btn--primary' : 'btn--quiet'}`}
                onClick={() => setPlacement('modal')}
              >
                A modal at sign-in
              </button>
            </div>

            {placement === 'modal' && (
              <>
                {/* Said before they choose the cards, not after they press Post.
                    034's rule — state the consequence while it can still change
                    the decision. */}
                <p className="noticemgr__warn">
                  A modal stops everyone. They have to close it before they reach
                  the hub, and it comes back for anybody who has not dismissed it.
                </p>

                {/* THE OPTIONS COME FROM `WELCOME_CARDS`, WHICH IS THE ONE PLACE
                    THE KEYS LIVE — the other half of the same contract as the
                    `banner_cta_keys_known` CHECK, asserted in both directions by
                    the web suite. A list typed out here would be the stale-
                    assertion trap: delete a card and nothing goes red, it just
                    stops being offerable. */}
                <p className="noticemgr__heading">Cards on it</p>
                <div className="noticemgr__actions">
                  {WELCOME_CARDS.map((c) => {
                    const on = ctaKeys.includes(c.key)
                    return (
                      <button
                        key={c.key}
                        type="button"
                        className={`btn btn--xs ${on ? 'btn--primary' : 'btn--quiet'}`}
                        aria-pressed={on}
                        title={c.body}
                        onClick={() =>
                          setCtaKeys((cur) =>
                            on ? cur.filter((k) => k !== c.key) : [...cur, c.key],
                          )
                        }
                      >
                        {c.title}
                      </button>
                    )
                  })}
                </div>
                <span className="noticemgr__hint">
                  {ctaKeys.length === 0
                    ? 'None chosen — the modal shows your words and nothing else, which is what the 7 October one does.'
                    : `${cardsFor(ctaKeys).map((c) => c.title).join(', ')}. They appear in that order.`}
                </span>
              </>
            )}
          </div>

          {/* Keyed on placement: switching to a modal must not pop one open
              because the strip preview happened to be showing. */}
          <HowItLooks
            key={placement}
            notice={lookOf({ tag, body, ctaLabel, ctaHref, dismissible, placement, ctaKeys })}
          />

          {/* Two buttons, not a checkbox — 046's move on the invite form. A
              checkbox states a preference about a button you have not pressed
              yet, so the form ends up with two controls for one decision. Here
              the choice IS the action: post it so people can put it away, or pin
              it so they cannot. */}
          <div className="noticemgr__actions">
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={!canSubmit}
              onClick={() => setDismissible(true)}
            >
              {busy ? 'Working…' : 'Post notice'}
            </button>
            <button
              type="submit"
              className="btn btn--quiet btn--sm"
              disabled={!canSubmit}
              onClick={() => setDismissible(false)}
              title="Nobody can dismiss it. It comes down on its own when the date passes."
            >
              Pin until it expires
            </button>
            <button
              type="button"
              className="btn btn--quiet btn--sm noticemgr__discard"
              disabled={busy}
              onClick={() => {
                resetForm()
                setCreating(false)
              }}
            >
              Discard
            </button>
          </div>
          {!canTarget && (
            <p className="noticemgr__hint">
              This goes to everyone in the tenant. Aiming a notice at part of the
              company needs the audience permission.
            </p>
          )}
        </form>
      </Dialog>
    </div>
  )
}
